import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Save, Settings, Database, Clock, Users, Shield, BarChart3, Copy, Check } from 'lucide-react';
import { getProjectSettings, updateProjectSettings, getCurrentUser } from '../services/api';

const ProjectSettings = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedProjectId, setCopiedProjectId] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_model: 'llama-3.1-8b-instant',
    auto_logging_enabled: true,
    retention_days: 90,
    max_prompt_versions: 50,
    allow_collaborators: true,
    require_approval_for_changes: false
  });

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [currentUser, projectData] = await Promise.all([
        getCurrentUser(),
        getProjectSettings(projectId)
      ]);
      
      setUser(currentUser);
      setProject(projectData);
      
      setFormData({
        name: projectData.name || '',
        description: projectData.description || '',
        default_model: projectData.default_model || 'llama-3.1-8b-instant',
        auto_logging_enabled: projectData.auto_logging_enabled !== false,
        retention_days: projectData.retention_days || 90,
        max_prompt_versions: projectData.max_prompt_versions || 50,
        allow_collaborators: projectData.allow_collaborators !== false,
        require_approval_for_changes: projectData.require_approval_for_changes || false
      });

    } catch (error) {
      console.error('Error loading project settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateProjectSettings(projectId, formData);
      navigate(`/projects/${projectId}`);
    } catch (error) {
      console.error('Error saving project settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Project not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
              <Link to="/" className="hover:text-primary-600">Dashboard</Link>
              <ChevronRight className="h-4 w-4" />
              <Link to={`/projects/${projectId}`} className="hover:text-primary-600">
                {project.name}
              </Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-gray-900">Settings</span>
            </nav>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Project Settings</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Manage your project configuration, policies, and default settings
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate(`/projects/${projectId}`)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white shadow-sm border rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Settings className="h-5 w-5 mr-2" />
                  Basic Information
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project ID
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={projectId}
                      readOnly
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-600 cursor-not-allowed"
                    />
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(projectId);
                          setCopiedProjectId(true);
                          setTimeout(() => setCopiedProjectId(false), 2000);
                        } catch (err) {
                          // Fallback for older browsers
                          const tempInput = document.createElement('input');
                          tempInput.value = projectId;
                          document.body.appendChild(tempInput);
                          tempInput.select();
                          document.execCommand('copy');
                          document.body.removeChild(tempInput);
                          setCopiedProjectId(true);
                          setTimeout(() => setCopiedProjectId(false), 2000);
                        }
                      }}
                      className="flex items-center justify-center px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                      title="Copy Project ID"
                    >
                      {copiedProjectId ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Describe what this project is for..."
                  />
                </div>
              </div>
            </div>

            {/* Default Settings */}
            <div className="bg-white shadow-sm border rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Database className="h-5 w-5 mr-2" />
                  Default Settings
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Model
                  </label>
                  <select
                    value={formData.default_model}
                    onChange={(e) => handleInputChange('default_model', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                                    <option value="llama-3.1-8b-instant">Llama 3 8B (Fast)</option>
                <option value="llama-3.3-70b-versatile">Llama 3 70B (Accurate)</option>
                <option value="gemma2-9b-it">Gemma 2</option>
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    Default model used for new prompts and evaluations
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Auto-logging for New Prompts</h4>
                    <p className="text-sm text-gray-500">
                      Enable SDK logging by default for new prompts
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.auto_logging_enabled}
                      onChange={(e) => handleInputChange('auto_logging_enabled', e.target.checked)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Data Retention */}
            <div className="bg-white shadow-sm border rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Data Retention & Limits
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Log Retention Period: {formData.retention_days} days
                  </label>
                  <input
                    type="range"
                    min="7"
                    max="365"
                    step="7"
                    value={formData.retention_days}
                    onChange={(e) => handleInputChange('retention_days', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>7 days</span>
                    <span>90 days</span>
                    <span>365 days</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Automatically delete logs older than this period
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Prompt Versions: {formData.max_prompt_versions}
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="200"
                    step="10"
                    value={formData.max_prompt_versions}
                    onChange={(e) => handleInputChange('max_prompt_versions', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10</span>
                    <span>100</span>
                    <span>200</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Maximum number of versions to keep per prompt
                  </p>
                </div>
              </div>
            </div>

            {/* Collaboration & Security */}
            <div className="bg-white shadow-sm border rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Shield className="h-5 w-5 mr-2" />
                  Collaboration & Security
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Allow Collaborators</h4>
                    <p className="text-sm text-gray-500">
                      Enable adding collaborators to prompts in this project
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.allow_collaborators}
                      onChange={(e) => handleInputChange('allow_collaborators', e.target.checked)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Require Approval for Changes</h4>
                    <p className="text-sm text-gray-500">
                      Require pull request approval before merging changes
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.require_approval_for_changes}
                      onChange={(e) => handleInputChange('require_approval_for_changes', e.target.checked)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Project Stats */}
            <div className="bg-white shadow-sm border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Project Statistics
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Prompts</span>
                  <span className="text-sm font-medium text-gray-900">
                    {project.stats?.prompt_count || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Test Sets</span>
                  <span className="text-sm font-medium text-gray-900">
                    {project.stats?.test_set_count || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Versions</span>
                  <span className="text-sm font-medium text-gray-900">
                    {project.stats?.version_count || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Logged Executions</span>
                  <span className="text-sm font-medium text-gray-900">
                    {project.stats?.log_count || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Project Info */}
            <div className="bg-white shadow-sm border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Project Info</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Created</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date(project.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Last Updated</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date(project.updated_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Owner</span>
                  <span className="text-sm font-medium text-gray-900">
                    {project.owner_name || 'Unknown'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Default Model</span>
                  <span className="text-sm font-medium text-gray-900">
                    {project.default_model || 'Llama 3 8B'}
                  </span>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="bg-white shadow-sm border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link
                  to={`/projects/${projectId}`}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <Settings className="h-4 w-4 inline mr-2" />
                  View Project
                </Link>
                <Link
                  to={`/projects/${projectId}/test-sets`}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <Database className="h-4 w-4 inline mr-2" />
                  Manage Test Sets
                </Link>
                <Link
                  to={`/logs?project=${projectId}`}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <BarChart3 className="h-4 w-4 inline mr-2" />
                  View Logs
                </Link>
                <Link
                  to="/api-keys"
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <Shield className="h-4 w-4 inline mr-2" />
                  Manage API Keys
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSettings; 